import { ChevronDown, ChevronsLeftRight, ChevronUp, Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MenuItem, MenuPopover } from '@/components/ui/Popover';
import { HIGHLIGHT_ZOOM_PRESETS } from '@/utils/fileHighlights';

interface FilePreviewDocToolbarProps {
  mode?: 'document' | 'media';
  page: number;
  pageCount: number;
  highlightIndex: number | null;
  highlightTotal: number;
  zoomPercent: number;
  fitActive: boolean;
  showHighlightNav: boolean;
  onPrevHighlight: () => void;
  onNextHighlight: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomPreset: (zoom: number) => void;
  onFitWidth: () => void;
  onMenuOpen: (open: boolean) => void;
}

export function FilePreviewDocToolbar({
  mode = 'document',
  page,
  pageCount,
  highlightIndex,
  highlightTotal,
  zoomPercent,
  fitActive,
  showHighlightNav,
  onPrevHighlight,
  onNextHighlight,
  onZoomIn,
  onZoomOut,
  onZoomPreset,
  onFitWidth,
  onMenuOpen,
}: FilePreviewDocToolbarProps) {
  const { t } = useTranslation();
  const fitLabel =
    mode === 'media' ? t('documents.preview.fit') : t('documents.highlights.fitWidth');
  return (
    <div className="fp-toolbar" data-fp-chrome>
      {mode === 'document' ? (
        <span className="fp-toolbar-page" aria-live="polite">
          {t('documents.highlights.pageOf', { page, total: Math.max(pageCount, 1) })}
        </span>
      ) : null}
      <div className="fp-toolbar-mid">
        {showHighlightNav && highlightIndex != null ? (
          <>
            <button
              type="button"
              className="fp-icon-btn is-sm"
              aria-label={t('common.previous')}
              onClick={onPrevHighlight}
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            <b>{t('documents.highlights.nOfN', { n: highlightIndex, total: highlightTotal })}</b>
            <button
              type="button"
              className="fp-icon-btn is-sm"
              aria-label={t('common.next')}
              onClick={onNextHighlight}
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </>
        ) : null}
      </div>
      <div className="fp-zoom" role="group" aria-label={t('documents.highlights.zoomMenu')}>
        <button
          type="button"
          className="fp-icon-btn is-sm"
          aria-label={t('documents.highlights.zoomOut')}
          onClick={onZoomOut}
        >
          <Minus className="h-4 w-4" />
        </button>
        <MenuPopover
          align="end"
          title={t('documents.highlights.zoomMenu')}
          trigger={
            <button
              type="button"
              className="fp-zoom-pct"
              aria-label={t('documents.highlights.zoomLabel', { percent: zoomPercent })}
              onClick={() => onMenuOpen(true)}
            >
              {zoomPercent} %
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          }
        >
          {HIGHLIGHT_ZOOM_PRESETS.map((preset) => (
            <MenuItem
              key={preset}
              active={!fitActive && Math.round(preset * 100) === zoomPercent}
              onClick={() => {
                onMenuOpen(false);
                onZoomPreset(preset);
              }}
            >
              {Math.round(preset * 100)} %
            </MenuItem>
          ))}
          <MenuItem
            active={fitActive}
            onClick={() => {
              onMenuOpen(false);
              onFitWidth();
            }}
          >
            {fitLabel}
          </MenuItem>
        </MenuPopover>
        <button
          type="button"
          className="fp-icon-btn is-sm"
          aria-label={t('documents.highlights.zoomIn')}
          onClick={onZoomIn}
        >
          <Plus className="h-4 w-4" />
        </button>
        <span className="fp-vr" />
        <button
          type="button"
          className="fp-zoom-fit"
          aria-pressed={fitActive}
          title={fitLabel}
          onClick={onFitWidth}
        >
          <ChevronsLeftRight className="h-4 w-4" />
          {fitLabel}
        </button>
      </div>
    </div>
  );
}
